import {
  t,
  TICKET_CATEGORIES,
  ticketCategoryLabel,
  ticketStatusLabel,
  timeAgo,
  type MyTicket,
} from '@procumi/shared';
import { useEffect, useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';
import { I } from './dashboard/shared';

const STATUS_CLS: Record<string, string> = { open: 'todo', pending: 'ok', solved: 'off' };

/**
 * „Ai nevoie de ajutor?”: omul ne scrie din aplicație (cu capturi de ecran) și vede aici
 * răspunsurile echipei Procumi. Răspunsul vine și pe mail. `openId`: întrebarea deschisă
 * din linkul din mail (`?ajutor=<id>`).
 */
export function SupportPanel({ openId }: { openId?: string }) {
  const { data, setData, reload } = useAsync<MyTicket[]>(() => api.supportList(), [], 'support');
  const [writing, setWriting] = useState(false);
  const [open, setOpen] = useState<string | undefined>(openId);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('question');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  useEffect(() => setOpen(openId), [openId]);

  const tickets = data ?? [];
  const current = tickets.find((x) => x.id === open);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const created = await api.supportCreate({
        subject: subject.trim(),
        category,
        body: body.trim(),
        files,
      });
      setData([created, ...tickets]);
      setSubject('');
      setBody('');
      setFiles([]);
      setCategory('question');
      setWriting(false);
      setSent(created.number);
      setOpen(created.id);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  const replace = (next: MyTicket) => setData(tickets.map((x) => (x.id === next.id ? next : x)));

  return (
    <aside className="help-contact">
      <span className="db-ico blue help-contact-i">
        <I d="mail" size={18} />
      </span>
      <h2>{t('Ai nevoie de ajutor?')}</h2>
      {sent ? (
        <p className="auth-ok" style={{ margin: 0 }}>
          {t(
            'Am primit întrebarea #{0}. Îți răspundem aici și pe mail, de obicei în aceeași zi lucrătoare.',
            sent,
          )}
        </p>
      ) : null}

      {writing ? (
        <form className="sup-form" onSubmit={submit}>
          <label className="f">
            <span>{t('Despre ce e vorba')}</span>
            <input
              className="inp"
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="f">
            <span>{t('Felul întrebării')}</span>
            <select className="inp" value={category} onChange={(e) => setCategory(e.target.value)}>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {ticketCategoryLabel(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="f">
            <span>{t('Ce încercai să faci și ce s-a întâmplat')}</span>
            <textarea
              className="inp"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
          </label>
          <label className="btn small sup-attach">
            {files.length ? t('{0} fișiere atașate', files.length) : t('Atașează o captură de ecran')}
            <input
              type="file"
              hidden
              multiple
              accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={(e) => setFiles([...(e.target.files ?? [])].slice(0, 3))}
            />
          </label>
          {err ? <p className="auth-err">{err}</p> : null}
          <div className="sup-acts">
            <button className="btn primary" disabled={busy}>
              {busy ? t('Se trimite…') : t('Trimite întrebarea')}
            </button>
            <button type="button" className="btn" onClick={() => setWriting(false)}>
              {t('Renunță')}
            </button>
          </div>
        </form>
      ) : (
        <>
          <p>
            {t(
              'Scrie-ne ce încercai să faci și ce s-a întâmplat. O captură de ecran ne ajută să răspundem mai repede.',
            )}
          </p>
          <button
            type="button"
            className="btn primary help-mail"
            onClick={() => (setWriting(true), setSent(null))}
          >
            <I d="mail" /> {t('Scrie-ne o întrebare')}
          </button>
        </>
      )}

      {tickets.length ? (
        <div className="sup-mine">
          <h3>{t('Întrebările tale')}</h3>
          <ul>
            {tickets.map((x) => (
              <li key={x.id}>
                <button
                  type="button"
                  aria-expanded={open === x.id}
                  onClick={() => setOpen(open === x.id ? undefined : x.id)}
                >
                  <span className="sup-subj">
                    #{x.number} · {x.subject}
                  </span>
                  <span className={`pill ${STATUS_CLS[x.status]}`}>{ticketStatusLabel(x.status, true)}</span>
                </button>
                {open === x.id && current ? (
                  <Conversation ticket={current} onChange={replace} onReload={reload} />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

function Conversation({
  ticket,
  onChange,
  onReload,
}: {
  ticket: MyTicket;
  onChange: (t: MyTicket) => void;
  onReload: () => void;
}) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // din linkul din mail: ia ultima variantă (poate a venit un răspuns între timp)
  useEffect(() => {
    api.supportTicket(ticket.id).then(onChange, () => onReload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      onChange(await api.supportReply(ticket.id, body.trim(), files));
      setBody('');
      setFiles([]);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sup-thread">
      {ticket.messages.map((m) => (
        <div key={m.id} className={`sup-msg ${m.author === 'staff' ? 'us' : ''}`}>
          <small>
            {m.author === 'staff' ? t('{0} · Procumi', m.name) : t('Tu')} · {timeAgo(m.createdAt)}
          </small>
          <p>{m.body}</p>
          {m.files.map((f) => (
            <a
              key={f.index}
              href={api.supportFileUrl(ticket.id, m.id, f.index)}
              target="_blank"
              rel="noreferrer"
            >
              {f.filename}
            </a>
          ))}
        </div>
      ))}
      <textarea
        className="inp"
        rows={3}
        value={body}
        placeholder={
          ticket.status === 'solved'
            ? t('Scrie dacă mai ai nevoie de ceva; o redeschidem.')
            : t('Răspunsul tău')
        }
        onChange={(e) => setBody(e.target.value)}
        aria-label={t('Răspunsul tău')}
      />
      {err ? <p className="auth-err">{err}</p> : null}
      <div className="sup-acts">
        <button type="button" className="btn primary small" disabled={busy || !body.trim()} onClick={send}>
          {t('Trimite')}
        </button>
        <label className="btn small">
          {files.length ? t('{0} fișiere', files.length) : t('Atașează')}
          <input
            type="file"
            hidden
            multiple
            accept="image/png,image/jpeg,image/webp,application/pdf"
            onChange={(e) => setFiles([...(e.target.files ?? [])].slice(0, 3))}
          />
        </label>
        {ticket.status !== 'solved' ? (
          <button
            type="button"
            className="btn small"
            onClick={async () => {
              try {
                onChange(await api.supportSolved(ticket.id));
              } catch (x) {
                setErr(errMsg(x));
              }
            }}
          >
            {t('Mi s-a rezolvat')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
